import React, { useState } from 'react';
import "./AntiSpamPolicy.css";

const AntiSpamPolicy = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedSections, setExpandedSections] = useState([]);
  const [activeFeature, setActiveFeature] = useState(0);

  const toggleSection = (sectionId) => {
    setExpandedSections(prev =>
      prev.includes(sectionId)
        ? prev.filter(id => id !== sectionId)
        : [...prev, sectionId]
    );
  };

  const policyData = {
    overview: {
      title: "AI-Powered Spam Protection System",
      description: "Enterprise-grade spam detection and prevention ensuring clean, secure communications across HR Copilot platform",
      stats: [
        { value: "99.9%", label: "Spam Detection Accuracy", icon: "🎯" },
        { value: "<50ms", label: "Average Response Time", icon: "⚡" },
        { value: "24/7", label: "Real-time Monitoring", icon: "🛡️" },
        { value: "1M+", label: "Patterns Analyzed", icon: "📊" }
      ],
      features: [
        {
          icon: "🤖",
          title: "Machine Learning Intelligence",
          description: "Advanced AI models trained on millions of spam patterns with continuous adaptive learning",
          metrics: "99.9% accuracy rate"
        },
        {
          icon: "🔍",
          title: "Behavioral Analysis Engine",
          description: "Real-time user behavior monitoring to detect and prevent suspicious activities",
          metrics: "95% anomaly detection"
        },
        {
          icon: "📧",
          title: "Content Filtering System",
          description: "Multi-layered content analysis with NLP and pattern recognition algorithms",
          metrics: "98.7% spam blocked"
        }
      ]
    },
    protections: [
      {
        id: "content-filtering",
        title: "Advanced Content Filtering",
        description: "Multi-layered content analysis using NLP and machine learning algorithms",
        icon: "📝",
        status: "active",
        metrics: {
          accuracy: "98.7%",
          speed: "<30ms",
          coverage: "All communications"
        },
        features: [
          "Natural Language Processing",
          "Pattern Recognition",
          "Contextual Analysis",
          "Semantic Understanding"
        ]
      },
      {
        id: "behavioral-analysis",
        title: "Behavioral Pattern Detection",
        description: "Real-time monitoring of user activities and behavior patterns",
        icon: "📈",
        status: "active",
        metrics: {
          accuracy: "99.2%",
          speed: "Real-time",
          coverage: "All user actions"
        },
        features: [
          "Activity Frequency Analysis",
          "Geolocation Verification",
          "Device Fingerprinting",
          "Rate Limiting"
        ]
      },
      {
        id: "ai-models",
        title: "AI/ML Spam Classification",
        description: "Advanced machine learning models for intelligent spam classification",
        icon: "🧠",
        status: "active",
        metrics: {
          accuracy: "99.9%",
          speed: "<100ms",
          coverage: "Continuous learning"
        },
        features: [
          "Neural Network Classification",
          "Anomaly Detection",
          "Pattern Recognition",
          "Adaptive Learning"
        ]
      },
      {
        id: "threat-intelligence",
        title: "Threat Intelligence Integration",
        description: "Global threat intelligence feeds and real-time threat updates",
        icon: "🌐",
        status: "active",
        metrics: {
          accuracy: "99.5%",
          speed: "<5min",
          coverage: "Global threats"
        },
        features: [
          "Real-time Threat Feeds",
          "Global Intelligence",
          "Threat Pattern Analysis",
          "Proactive Protection"
        ]
      }
    ],
    compliance: {
      standards: [
        {
          name: "GDPR Compliance",
          status: "fully-compliant",
          description: "Full compliance with EU General Data Protection Regulation requirements",
          icon: "🇪🇺",
          features: ["Data Encryption", "Right to Erasure", "Data Portability", "Privacy by Design"]
        },
        {
          name: "CCPA Standards",
          status: "fully-compliant",
          description: "California Consumer Privacy Act compliance with data protection measures",
          icon: "🔒",
          features: ["Consumer Rights", "Data Transparency", "Opt-out Mechanisms", "Security Safeguards"]
        },
        {
          name: "SOC 2 Type II",
          status: "certified",
          description: "Security, availability, and confidentiality certification achieved",
          icon: "🏆",
          features: ["Security Controls", "Availability Monitoring", "Confidentiality Assurance", "Privacy Protection"]
        },
        {
          name: "ISO 27001",
          status: "certified",
          description: "International standard for information security management",
          icon: "🌍",
          features: ["Risk Management", "Security Policies", "Access Control", "Incident Management"]
        }
      ]
    },
    reporting: {
      metrics: [
        {
          title: "Spam Detection Rate",
          value: "99.9%",
          trend: "+2.1%",
          target: "100%",
          progress: 99.9
        },
        {
          title: "False Positives",
          value: "0.1%",
          trend: "-0.3%",
          target: "<0.5%",
          progress: 80
        },
        {
          title: "Response Time",
          value: "<50ms",
          trend: "-10ms",
          target: "<100ms",
          progress: 100
        },
        {
          title: "System Uptime",
          value: "99.99%",
          trend: "+0.01%",
          target: "99.95%",
          progress: 100
        }
      ],
      features: [
        {
          title: "Real-time Dashboard",
          description: "Live monitoring of spam activities and system performance",
          icon: "📊"
        },
        {
          title: "Custom Reports",
          description: "Generate detailed compliance and security reports",
          icon: "📋"
        },
        {
          title: "Alert System",
          description: "Instant notifications for suspicious activities",
          icon: "🚨"
        }
      ]
    }
  };

  return (
    <div className="hrspam-policy-container">
      {/* Enhanced Background Elements */}
      <div className="hrspam-bg-elements">
        <div className="hrspam-orb hrspam-orb-1"></div>
        <div className="hrspam-orb hrspam-orb-2"></div>
        <div className="hrspam-orb hrspam-orb-3"></div>
        <div className="hrspam-grid-overlay"></div>
        <div className="hrspam-floating-shapes">
          <div className="hrspam-shape hrspam-shape-1"></div>
          <div className="hrspam-shape hrspam-shape-2"></div>
          <div className="hrspam-shape hrspam-shape-3"></div>
        </div>
        <div className="hrspam-particles">
          {[...Array(15)].map((_, i) => (
            <div key={i} className="hrspam-particle" style={{
              '--delay': `${i * 0.5}s`,
              '--size': `${Math.random() * 4 + 2}px`,
              '--x': `${Math.random() * 100}%`,
              '--y': `${Math.random() * 100}%`
            }}></div>
          ))}
        </div>
      </div>

      <div className="hrspam-content-wrapper">
        {/* Enhanced Header Section */}
        <header className="hrspam-policy-header">
          <div className="hrspam-header-content">
            <div className="hrspam-badge-wrapper">
              <span className="hrspam-security-badge">
                <span className="hrspam-badge-icon">🛡️</span>
                Enterprise Security Framework
                <span className="hrspam-badge-glow"></span>
                <span className="hrspam-badge-pulse"></span>
              </span>
            </div>
            
            <h1 className="hrspam-main-title">
              Advanced Anti-Spam
              <span className="hrspam-title-accent"> Protection System</span>
            </h1>
            
            <p className="hrspam-header-description">
              Comprehensive AI-powered spam detection and prevention system designed specifically for 
              HR Copilot. Advanced machine learning algorithms ensure clean, secure communications 
              while maintaining maximum productivity and compliance.
            </p>

            {/* Enhanced Security Stats */}
            <div className="hrspam-security-stats">
              {policyData.overview.stats.map((stat, index) => (
                <div key={index} className="hrspam-stat-card">
                  <div className="hrspam-stat-icon">{stat.icon}</div>
                  <div className="hrspam-stat-content">
                    <div className="hrspam-stat-value">{stat.value}</div>
                    <div className="hrspam-stat-label">{stat.label}</div>
                  </div>
                  <div className="hrspam-stat-glow"></div>
                  <div className="hrspam-stat-pulse"></div>
                </div>
              ))}
            </div>

            {/* Trust Indicators */}
            <div className="hrspam-trust-indicators">
              <div className="hrspam-trust-item">
                <div className="hrspam-trust-icon">✅</div>
                <span>Enterprise-Grade Security</span>
              </div>
              <div className="hrspam-trust-item">
                <div className="hrspam-trust-icon">🔐</div>
                <span>End-to-End Encryption</span>
              </div>
              <div className="hrspam-trust-item">
                <div className="hrspam-trust-icon">🌐</div>
                <span>Global Compliance</span>
              </div>
              <div className="hrspam-trust-item">
                <div className="hrspam-trust-icon">⚡</div>
                <span>Real-time Protection</span>
              </div>
            </div>
          </div>
        </header>

        {/* Enhanced Navigation Tabs */}
        <nav className="hrspam-policy-nav">
          <div className="hrspam-nav-container">
            {[
              { id: 'overview', label: 'System Overview', icon: '📋', description: 'Core features' },
              { id: 'protections', label: 'Security Layers', icon: '🛡️', description: 'Multi-layer protection' },
              { id: 'compliance', label: 'Compliance', icon: '📊', description: 'Regulatory standards' },
              { id: 'reporting', label: 'Analytics', icon: '📈', description: 'Reports & insights' }
            ].map(tab => (
              <button
                key={tab.id}
                className={`hrspam-nav-tab ${activeTab === tab.id ? 'hrspam-nav-active' : ''}`}
                onClick={() => setActiveTab(tab.id)}
              >
                <div className="hrspam-tab-content">
                  <span className="hrspam-tab-icon">{tab.icon}</span>
                  <div className="hrspam-tab-text">
                    <span className="hrspam-tab-label">{tab.label}</span>
                    <span className="hrspam-tab-desc">{tab.description}</span>
                  </div>
                </div>
                <div className="hrspam-tab-indicator"></div>
                <div className="hrspam-tab-glow"></div>
              </button>
            ))}
          </div>
        </nav>

        {/* Main Content Area */}
        <main className="hrspam-policy-main">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="hrspam-tab-content">
              <div className="hrspam-overview-section">
                <div className="hrspam-section-header">
                  <h2>AI-Powered Protection System</h2>
                  <p>Advanced spam detection leveraging cutting-edge artificial intelligence and machine learning technologies</p>
                </div>

                <div className="hrspam-system-architecture">
                  <div className="hrspam-architecture-visual">
                    <div className="hrspam-arch-layer hrspam-layer-1">
                      <div className="hrspam-layer-icon">📧</div>
                      <div className="hrspam-layer-content">
                        <h4>Input Processing</h4>
                        <p>Real-time message ingestion and preprocessing</p>
                      </div>
                      <div className="hrspam-layer-connector"></div>
                    </div>
                    
                    <div className="hrspam-arch-layer hrspam-layer-2">
                      <div className="hrspam-layer-icon">🤖</div>
                      <div className="hrspam-layer-content">
                        <h4>AI Analysis</h4>
                        <p>Machine learning classification and pattern recognition</p>
                      </div>
                      <div className="hrspam-layer-connector"></div>
                    </div>
                    
                    <div className="hrspam-arch-layer hrspam-layer-3">
                      <div className="hrspam-layer-icon">🛡️</div>
                      <div className="hrspam-layer-content">
                        <h4>Threat Prevention</h4>
                        <p>Automated blocking and security measures</p>
                      </div>
                      <div className="hrspam-layer-connector"></div>
                    </div>
                    
                    <div className="hrspam-arch-layer hrspam-layer-4">
                      <div className="hrspam-layer-icon">📊</div>
                      <div className="hrspam-layer-content">
                        <h4>Reporting & Analytics</h4>
                        <p>Comprehensive insights and performance monitoring</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hrspam-features-showcase">
                  {policyData.overview.features.map((feature, index) => (
                    <div 
                      key={index}
                      className={`hrspam-feature-card ${activeFeature === index ? 'hrspam-feature-active' : ''}`}
                      onMouseEnter={() => setActiveFeature(index)}
                    >
                      <div className="hrspam-feature-visual">
                        <div className="hrspam-feature-orb"></div>
                        <span className="hrspam-feature-icon">{feature.icon}</span>
                        <div className="hrspam-feature-ripple"></div>
                        <div className="hrspam-feature-sparkles">
                          {[...Array(3)].map((_, i) => (
                            <div key={i} className="hrspam-sparkle" style={{animationDelay: `${i * 0.3}s`}}></div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="hrspam-feature-content">
                        <h3>{feature.title}</h3>
                        <p>{feature.description}</p>
                        <div className="hrspam-feature-metric">
                          <span className="hrspam-metric-value">{feature.metrics}</span>
                          <div className="hrspam-metric-bar">
                            <div className="hrspam-metric-fill"></div>
                          </div>
                        </div>
                      </div>
                      
                      <div className="hrspam-feature-glow"></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Protections Tab */}
          {activeTab === 'protections' && (
            <div className="hrspam-tab-content">
              <div className="hrspam-protections-section">
                <div className="hrspam-section-header">
                  <h2>Multi-Layer Security Protection</h2>
                  <p>Comprehensive defense mechanisms working together to provide enterprise-grade spam protection</p>
                </div>

                <div className="hrspam-protections-grid">
                  {policyData.protections.map((protection, index) => (
                    <div 
                      key={protection.id}
                      className={`hrspam-protection-card ${
                        expandedSections.includes(protection.id) ? 'hrspam-protection-expanded' : ''
                      }`}
                    >
                      <div className="hrspam-protection-header">
                        <div className="hrspam-protection-main">
                          <div className="hrspam-protection-visual">
                            <div className="hrspam-protection-orb"></div>
                            <span className="hrspam-protection-icon">{protection.icon}</span>
                            <div className="hrspam-protection-status">
                              <span className={`hrspam-status-indicator hrspam-status-${protection.status}`}></span>
                            </div>
                          </div>
                          
                          <div className="hrspam-protection-info">
                            <h3>{protection.title}</h3>
                            <p>{protection.description}</p>
                            
                            <div className="hrspam-protection-metrics">
                              <div className="hrspam-protection-metric">
                                <span className="hrspam-metric-label">Accuracy</span>
                                <span className="hrspam-metric-value">{protection.metrics.accuracy}</span>
                              </div>
                              <div className="hrspam-protection-metric">
                                <span className="hrspam-metric-label">Speed</span>
                                <span className="hrspam-metric-value">{protection.metrics.speed}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        <button 
                          className="hrspam-expand-btn"
                          onClick={() => toggleSection(protection.id)}
                        >
                          <span className="hrspam-expand-icon">
                            {expandedSections.includes(protection.id) ? '−' : '+'}
                          </span>
                        </button>
                      </div>

                      {expandedSections.includes(protection.id) && (
                        <div className="hrspam-protection-details">
                          <div className="hrspam-details-content">
                            <div className="hrspam-features-list">
                              <h4>Key Features</h4>
                              <div className="hrspam-features-grid">
                                {protection.features.map((feature, featureIndex) => (
                                  <div key={featureIndex} className="hrspam-feature-item">
                                    <span className="hrspam-feature-bullet"></span>
                                    <span>{feature}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                            
                            <div className="hrspam-coverage-info">
                              <div className="hrspam-coverage-metric">
                                <span className="hrspam-coverage-label">Coverage</span>
                                <span className="hrspam-coverage-value">{protection.metrics.coverage}</span>
                              </div>
                              <div className="hrspam-performance-chart">
                                <div className="hrspam-chart-bar" style={{height: '90%'}}>
                                  <div className="hrspam-chart-fill"></div>
                                </div>
                                <div className="hrspam-chart-label">Performance</div>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Compliance Tab */}
          {activeTab === 'compliance' && (
            <div className="hrspam-tab-content">
              <div className="hrspam-compliance-section">
                <div className="hrspam-section-header">
                  <h2>Regulatory Compliance & Standards</h2>
                  <p>Full compliance with global data protection regulations and industry security standards</p>
                </div>

                <div className="hrspam-compliance-showcase">
                  {policyData.compliance.standards.map((standard, index) => (
                    <div key={index} className="hrspam-compliance-card">
                      <div className="hrspam-compliance-header">
                        <div className="hrspam-compliance-icon">{standard.icon}</div>
                        <div className="hrspam-compliance-badge">
                          <span className={`hrspam-compliance-status hrspam-status-${standard.status}`}>
                            {standard.status.replace('-', ' ')}
                          </span>
                        </div>
                      </div>
                      
                      <div className="hrspam-compliance-content">
                        <h3>{standard.name}</h3>
                        <p>{standard.description}</p>
                        
                        <div className="hrspam-compliance-features">
                          {standard.features.map((feature, featureIndex) => (
                            <div key={featureIndex} className="hrspam-compliance-feature">
                              <span className="hrspam-feature-check">✓</span>
                              <span>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                      
                      <div className="hrspam-compliance-glow"></div>
                    </div>
                  ))}
                </div>

                <div className="hrspam-compliance-footer">
                  <div className="hrspam-compliance-stats">
                    <div className="hrspam-compliance-stat">
                      <div className="hrspam-stat-number">100%</div>
                      <div className="hrspam-stat-label">Compliance Coverage</div>
                    </div>
                    <div className="hrspam-compliance-stat">
                      <div className="hrspam-stat-number">24/7</div>
                      <div className="hrspam-stat-label">Monitoring</div>
                    </div>
                    <div className="hrspam-compliance-stat">
                      <div className="hrspam-stat-number">0</div>
                      <div className="hrspam-stat-label">Compliance Issues</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Reporting Tab */}
          {activeTab === 'reporting' && (
            <div className="hrspam-tab-content">
              <div className="hrspam-reporting-section">
                <div className="hrspam-section-header">
                  <h2>Advanced Analytics & Reporting</h2>
                  <p>Comprehensive insights and real-time monitoring of spam protection performance</p>
                </div>

                <div className="hrspam-reporting-dashboard">
                  <div className="hrspam-metrics-grid">
                    {policyData.reporting.metrics.map((metric, index) => (
                      <div key={index} className="hrspam-metric-card">
                        <div className="hrspam-metric-header">
                          <span className="hrspam-metric-title">{metric.title}</span>
                          <span className={`hrspam-metric-trend ${
                            metric.trend.startsWith('+') ? 'hrspam-trend-positive' : 'hrspam-trend-negative'
                          }`}>
                            {metric.trend}
                          </span>
                        </div>
                        
                        <div className="hrspam-metric-value">{metric.value}</div>
                        
                        <div className="hrspam-metric-progress">
                          <div className="hrspam-progress-info">
                            <span>Target: {metric.target}</span>
                            <span>{metric.progress}%</span>
                          </div>
                          <div className="hrspam-progress-bar">
                            <div 
                              className="hrspam-progress-fill" 
                              style={{width: `${metric.progress}%`}}
                            >
                              <div className="hrspam-progress-glow"></div>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hrspam-reporting-features">
                    {policyData.reporting.features.map((feature, index) => (
                      <div key={index} className="hrspam-reporting-feature">
                        <div className="hrspam-feature-icon">{feature.icon}</div>
                        <div className="hrspam-feature-content">
                          <h4>{feature.title}</h4>
                          <p>{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="hrspam-reporting-actions">
                    <button className="hrspam-action-btn hrspam-btn-primary">
                      <span>Generate Comprehensive Report</span>
                      <div className="hrspam-btn-glow"></div>
                    </button>
                    <button className="hrspam-action-btn hrspam-btn-secondary">
                      <span>Export Analytics Data</span>
                    </button>
                    <button className="hrspam-action-btn hrspam-btn-outline">
                      <span>Schedule Automated Reports</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </main>

        {/* Enhanced Footer CTA */}
        <footer className="hrspam-policy-footer">
          <div className="hrspam-footer-content">
            <div className="hrspam-footer-main">
              <div className="hrspam-footer-text">
                <h3>Ready to Enhance Your Security?</h3>
                <p>Get started with enterprise-grade spam protection and ensure secure communications across your organization</p>
              </div>
              
              <div className="hrspam-footer-actions">
                <button className="hrspam-cta-btn hrspam-cta-primary">
                  <span>Start Free Trial</span>
                  <div className="hrspam-cta-glow"></div>
                </button>
                <button className="hrspam-cta-btn hrspam-cta-secondary">
                  <span>Schedule Demo</span>
                </button>
              </div>
            </div>
            
            <div className="hrspam-footer-features">
              <div className="hrspam-footer-feature">
                <span className="hrspam-feature-icon">🚀</span>
                <span>Setup in 5 minutes</span>
              </div>
              <div className="hrspam-footer-feature">
                <span className="hrspam-feature-icon">💼</span>
                <span>Enterprise ready</span>
              </div>
              <div className="hrspam-footer-feature">
                <span className="hrspam-feature-icon">🔧</span>
                <span>24/7 Support</span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default AntiSpamPolicy;